import React, { useState, useEffect } from 'react';
import { Clock, Film, ThumbsUp, Trophy, TrendingUp, Star, Calendar } from 'lucide-react';
import apiService from '../services/apiService';

const UserStats = ({ user }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (user) {
            loadStats();
        }
    }, [user]);

    const loadStats = async () => {
        try {
            setLoading(true);
            const response = await apiService.getUserStats();

            if (response.success) {
                setStats(response.data);
            } else {
                setError('Impossible de charger les statistiques');
            }
        } catch (err) {
            console.error('Error loading stats:', err);
            setError('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (error || !stats) {
        return (
            <div className="text-center py-10">
                <p className="text-gray-400">{error || 'Aucune statistique disponible'}</p>
            </div>
        );
    }

    const { viewing_time, movies, ratings, favorite_genres, top_movies, ranking, monthly_activity } = stats;

    return (
        <div className="space-y-6">
            {/* Cartes principales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Temps de visionnage */}
                <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-xl p-6 border border-purple-500/30">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-purple-600 rounded-lg">
                            <Clock className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm">Temps total</p>
                            <p className="text-3xl font-bold text-white">{viewing_time.total_hours}h</p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">{viewing_time.total_minutes} minutes passées devant l'écran</p>
                </div>

                {/* Films vus */}
                <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-6 border border-blue-500/30">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-blue-600 rounded-lg">
                            <Film className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm">Films vus</p>
                            <p className="text-3xl font-bold text-white">{movies.total_watched}</p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        {movies.films} films · {movies.series} épisodes
                    </p>
                </div>

                {/* Films aimés */}
                <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 rounded-xl p-6 border border-green-500/30">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-3 bg-green-600 rounded-lg">
                            <ThumbsUp className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm">Films aimés</p>
                            <p className="text-3xl font-bold text-white">{ratings.liked}</p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">{ratings.disliked} films détestés</p>
                </div>

                {/* Classement */}
                <div className={`bg-gradient-to-br rounded-xl p-6 border ${
                    ranking.is_top_3
                        ? 'from-yellow-600/20 to-yellow-800/20 border-yellow-500/30'
                        : 'from-gray-600/20 to-gray-800/20 border-gray-500/30'
                }`}>
                    <div className="flex items-center gap-3 mb-3">
                        <div className={`p-3 rounded-lg ${ranking.is_top_3 ? 'bg-yellow-600' : 'bg-gray-600'}`}>
                            <Trophy className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-gray-400 text-sm">Classement</p>
                            <p className="text-3xl font-bold text-white">#{ranking.position}</p>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500">
                        {ranking.is_top_3 && '🔥 Tu es dans le TOP 3 !'}
                        {!ranking.is_top_3 && ranking.is_top_10 && '⭐ Tu es dans le TOP 10 !'}
                        {!ranking.is_top_10 && `Top ${ranking.percentile}% des spectateurs`}
                    </p>
                </div>
            </div>

            {/* Genres préférés */}
            {favorite_genres && favorite_genres.length > 0 && (
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                        <Star className="w-5 h-5 mr-2 text-yellow-500" />
                        Tes genres préférés
                    </h3>
                    <div className="space-y-3">
                        {favorite_genres.map((genre, index) => (
                            <div key={index}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-white font-medium text-sm">{genre.genre}</span>
                                    <span className="text-gray-400 text-sm">{genre.percentage}%</span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-2">
                                    <div
                                        className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${genre.percentage}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Top 5 films les plus vus */}
            {top_movies && top_movies.length > 0 && (
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                        <TrendingUp className="w-5 h-5 mr-2 text-blue-500" />
                        Tes films favoris
                    </h3>
                    <div className="space-y-3">
                        {top_movies.map((movie, index) => (
                            <div key={movie.id} className="flex items-center gap-4 bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors">
                                {/* Position */}
                                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                                    index === 0 ? 'bg-yellow-600 text-white' :
                                        index === 1 ? 'bg-gray-400 text-white' :
                                            index === 2 ? 'bg-orange-600 text-white' :
                                                'bg-white/10 text-gray-400'
                                }`}>
                                    {index + 1}
                                </div>

                                {/* Poster */}
                                <img
                                    src={movie.poster}
                                    alt={movie.title}
                                    className="w-12 h-16 object-cover rounded shadow-lg"
                                />

                                {/* Infos */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-white font-bold text-sm truncate">{movie.title}</p>
                                    <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                                        {movie.year && <span>{movie.year}</span>}
                                        {movie.rating && (
                                            <>
                                                <span>•</span>
                                                <span className="flex items-center gap-1">
                                                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                                                    {parseFloat(movie.rating).toFixed(1)}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Nombre de fois vu */}
                                <div className="flex-shrink-0 text-right">
                                    <p className="text-white font-bold text-lg">{movie.watch_count}×</p>
                                    <p className="text-xs text-gray-500">visionné{movie.watch_count > 1 ? 's' : ''}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Activité mensuelle */}
            {monthly_activity && monthly_activity.length > 0 && (
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-green-500" />
                        Activité des 6 derniers mois
                    </h3>
                    <div className="space-y-3">
                        {monthly_activity.map((month) => (
                            <div key={month.month}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-white font-medium text-sm">{month.month_name}</span>
                                    <span className="text-gray-400 text-sm">{month.count} film{month.count > 1 ? 's' : ''}</span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-2">
                                    <div
                                        className="bg-gradient-to-r from-green-600 to-teal-600 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${Math.min((month.count / Math.max(...monthly_activity.map(m => m.count))) * 100, 100)}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserStats;